/**
 * Workflow execution detail view for monitoring and user intervention
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Stack,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemContent,
  Modal,
  ModalDialog,
  ModalClose,
  Textarea,
} from '@mui/joy';
import {
  Play as PlayArrow,
  CheckCircle,
  XCircle as Error,
  Warning,
  Clock as Schedule,
  Stop,
} from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';

import type { WorkflowExecution, WorkflowNodeFailure } from '../../types/workflows';

interface WorkflowExecutionDetailProps {
  execution: WorkflowExecution;
  onUserDecision?: (failureId: number, decision: 'continue' | 'abort') => Promise<void>;
}

export default function WorkflowExecutionDetail({
  execution,
  onUserDecision,
}: WorkflowExecutionDetailProps) {
  const [selectedFailure, setSelectedFailure] = useState<WorkflowNodeFailure | null>(null);
  const [isDecisionModalOpen, setIsDecisionModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running':
        return <PlayArrow color="var(--joy-palette-primary-500)" />;
      case 'completed':
        return <CheckCircle color="var(--joy-palette-success-500)" />;
      case 'failed':
        return <Error color="var(--joy-palette-danger-500)" />;
      case 'waiting':
        return <Schedule color="var(--joy-palette-warning-500)" />;
      case 'user_intervention':
        return <Warning color="var(--joy-palette-warning-500)" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string): 'primary' | 'success' | 'danger' | 'warning' | 'neutral' => {
    switch (status) {
      case 'running':
        return 'primary';
      case 'completed':
        return 'success';
      case 'failed':
        return 'danger';
      case 'waiting':
      case 'user_intervention':
        return 'warning';
      default:
        return 'neutral';
    }
  };

  const handleUserDecision = async (decision: 'continue' | 'abort') => {
    if (!selectedFailure || !onUserDecision) return;

    setIsProcessing(true);
    try {
      await onUserDecision(selectedFailure.id, decision);
      setIsDecisionModalOpen(false);
      setSelectedFailure(null);
    } catch (error) {
      console.error('Error submitting user decision:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const openDecisionModal = (failure: WorkflowNodeFailure) => {
    setSelectedFailure(failure);
    setIsDecisionModalOpen(true);
  };

  const unresolvedFailures = execution.failures?.filter((f: WorkflowNodeFailure) => !f.user_decision) || [];
  const requiresIntervention = execution.status === 'user_intervention' && unresolvedFailures.length > 0;

  return (
    <Box>
      {/* Header */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
            {getStatusIcon(execution.status)}
            <Box sx={{ flex: 1 }}>
              <Typography level="h3">{execution.automation_name}</Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                Execution ID: {execution.id}
              </Typography>
            </Box>
            <Chip color={getStatusColor(execution.status)} variant="soft">
              {execution.status.replace('_', ' ').toUpperCase()}
            </Chip>
          </Stack>

          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Box>
              <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                Started
              </Typography>
              <Typography level="body-sm">
                {formatDistanceToNow(new Date(execution.created_at), { addSuffix: true })}
              </Typography>
            </Box>

            {execution.resume_at && (
              <Box>
                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                  Resume At
                </Typography>
                <Typography level="body-sm">
                  {formatDistanceToNow(new Date(execution.resume_at), { addSuffix: true })}
                </Typography>
              </Box>
            )}

            <Box>
              <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                Current Node
              </Typography>
              <Typography level="body-sm">
                {execution.current_node_type} (ID: {execution.current_node_id})
              </Typography>
            </Box>

            {execution.retry_count > 0 && (
              <Box>
                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                  Retries
                </Typography>
                <Typography level="body-sm">{execution.retry_count}</Typography>
              </Box>
            )}
          </Stack>

          {execution.error_message && (
            <Alert color="danger" sx={{ mt: 2 }}>
              {execution.error_message}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* User Intervention Required */}
      {requiresIntervention && (
        <Alert
          color="warning"
          startDecorator={<Warning />}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography level="title-md" sx={{ mb: 1 }}>
              User Intervention Required
            </Typography>
            <Typography level="body-sm">
              This workflow has encountered errors and needs your decision to continue or abort.
            </Typography>
          </Box>
        </Alert>
      )}

      {/* Failures List */}
      {execution.failures && execution.failures.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography level="title-lg" sx={{ mb: 2 }}>
              Workflow Failures ({execution.failures.length})
            </Typography>

            <List>
              {execution.failures.map((failure: WorkflowNodeFailure, index: number) => (
                <React.Fragment key={failure.id}>
                  {index > 0 && <Divider />}
                  <ListItem>
                    <ListItemContent>
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Error color="var(--joy-palette-danger-500)" />
                          <Box sx={{ flex: 1 }}>
                            <Typography level="title-sm">
                              {failure.node_type} (Node ID: {failure.node_id})
                            </Typography>
                            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                              {formatDistanceToNow(new Date(failure.created_at), { addSuffix: true })}
                            </Typography>
                          </Box>

                          {failure.user_decision ? (
                            <Chip
                              color={failure.user_decision === 'continue' ? 'success' : 'danger'}
                              variant="soft"
                            >
                              {failure.user_decision.toUpperCase()}
                            </Chip>
                          ) : (
                            <Button
                              size="sm"
                              variant="soft"
                              color="warning"
                              onClick={() => openDecisionModal(failure)}
                            >
                              Make Decision
                            </Button>
                          )}
                        </Stack>

                        <Alert color="danger" variant="soft">
                          {failure.error_message}
                        </Alert>

                        <Stack direction="row" spacing={2}>
                          <Typography level="body-xs">
                            Retry Count: {failure.retry_count}
                          </Typography>
                          {failure.user_notified && (
                            <Typography level="body-xs" sx={{ color: 'success.500' }}>
                              User Notified
                            </Typography>
                          )}
                          {failure.resolved_at && (
                            <Typography level="body-xs">
                              Resolved: {formatDistanceToNow(new Date(failure.resolved_at), { addSuffix: true })}
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                    </ListItemContent>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Context Data */}
      {execution.context && Object.keys(execution.context).length > 0 && (
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent>
            <Typography level="title-lg" sx={{ mb: 2 }}>
              Execution Context
            </Typography>
            <Textarea
              readOnly
              value={JSON.stringify(execution.context, null, 2)}
              minRows={5}
              maxRows={15}
              sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
            />
          </CardContent>
        </Card>
      )}

      {/* User Decision Modal */}
      <Modal open={isDecisionModalOpen} onClose={() => setIsDecisionModalOpen(false)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 2 }}>
            Workflow Intervention Required
          </Typography>

          {selectedFailure && (
            <>
              <Alert color="danger" sx={{ mb: 2 }}>
                {selectedFailure.error_message}
              </Alert>

              <Typography level="body-sm" sx={{ mb: 2 }}>
                Node: {selectedFailure.node_type} (ID: {selectedFailure.node_id})
              </Typography>

              <Typography level="body-sm" sx={{ mb: 2 }}>
                This node has failed {selectedFailure.retry_count} time(s). How would you like to proceed?
              </Typography>

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  color="danger"
                  variant="outlined"
                  startDecorator={<Stop />}
                  onClick={() => handleUserDecision('abort')}
                  loading={isProcessing}
                >
                  Abort Workflow
                </Button>
                <Button
                  color="success"
                  startDecorator={<PlayArrow />}
                  onClick={() => handleUserDecision('continue')}
                  loading={isProcessing}
                >
                  Retry & Continue
                </Button>
              </Stack>
            </>
          )}
        </ModalDialog>
      </Modal>
    </Box>
  );
}
